/*
 * Rental Agreement step: DocuSign signing via same-tab navigation.
 * (DocuSign CSP frame-ancestors prevents iframe embedding.)
 */
import { LoadingButton } from '@mui/lab';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { brandConfig } from 'src/config';
import { GuestGeneral } from 'src/models/GuestGeneral';
import { ReservationGeneral } from 'src/models/ReservationGeneral';
import { UserAccountGeneral } from 'src/models/UserAccountGeneral';
import FormProvider from '../../components/hook-form/FormProvider';
import {
  usePostPrepareEmbeddedRentalAgreementMutation,
  usePostStartRentalAgreementSigningMutation,
} from '../../redux/rtkQuery/apiSlice';
import { PATH_APP } from '../../routes/paths';
import {
  consumeDocuSignReturnEvent,
  getPersistedEnvelopeId,
  persistEnvelopeId as persistEnvelopeIdToStorage,
  setRentalAgreementResignPending,
} from './docusignReturnStorage';
import HelpLink from './HelpLinks';
import { RegistrationFooter } from './RegistrationFooter';

type SigningPhase = 'preparing' | 'redirecting' | 'error';

const ID_VERIFICATION_FAILED_MESSAGE =
  'Identity verification has failed. Please try again, or contact Guest Experience if the problem continues.';

const RESEND_COOLDOWN_MS = 60 * 1000;

const HOST_EMAIL_REQUIRED_SNACKBAR =
  "Registration cannot be completed without the host's email address. Please contact Guest Experience.";

interface RegistrationRentalAgreementFormProps {
  stepCompleted: boolean;
  stepFinal: boolean;
  reservation: ReservationGeneral;
  callbackContinue: VoidFunction;
  callbackCancel: VoidFunction;
  callbackStartOver: VoidFunction;
  enforceSessionIsMain: boolean;
  mainGuest: GuestGeneral | null;
  sessionUser: UserAccountGeneral | null;
  guestsLoading?: boolean;
}

export function RegistrationRentalAgreementForm({
  stepCompleted,
  stepFinal,
  reservation,
  callbackContinue,
  callbackCancel,
  callbackStartOver,
  enforceSessionIsMain,
  mainGuest,
  sessionUser,
  guestsLoading = false,
}: RegistrationRentalAgreementFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const hasAttemptedAutoPrepare = useRef(false);
  const missingHostEmailSnackShownRef = useRef(false);
  const cooldownTimerId = useRef<ReturnType<typeof setInterval> | null>(null);

  const [phase, setPhase] = useState<SigningPhase>('preparing');
  const [envelopeId, setEnvelopeId] = useState<string | null>(() =>
    getPersistedEnvelopeId(reservation.name)
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [needsFullResend, setNeedsFullResend] = useState(false);
  const [resendCooldownUntil, setResendCooldownUntil] = useState(0);
  const [, setCooldownTick] = useState(0);

  const returnUrl = PATH_APP.createDocuSignReturnUrl(reservation.name);

  const methods = useForm({
    defaultValues: {},
    resolver: yupResolver(Yup.object().shape({})),
    mode: 'all',
  });

  const [prepareEmbedded, { isLoading: prepareLoading }] =
    usePostPrepareEmbeddedRentalAgreementMutation();
  const [startSigning, { isLoading: startLoading }] = usePostStartRentalAgreementSigningMutation();

  const persistEnvelopeId = useCallback(
    (id: string | null) => {
      setEnvelopeId(id);
      persistEnvelopeIdToStorage(reservation.name, id);
    },
    [reservation.name]
  );

  const applyReturnEvent = useCallback(
    (eventName: string) => {
      // signing_complete is handled by the wizard (resume Identity Verification + SF patch).
      if (eventName === 'signing_complete') {
        persistEnvelopeId(null);
        callbackContinue();
        return;
      }

      if (eventName === 'cancel') {
        setPhase('error');
        setErrorMessage("Signing was canceled. You can try again when you're ready.");
        setNeedsFullResend(false);
        return;
      }
      if (eventName === 'decline') {
        setPhase('error');
        setErrorMessage('Signing was declined.');
        setNeedsFullResend(false);
        return;
      }
      if (eventName === 'ttl_expired' || eventName === 'session_timeout') {
        setPhase('error');
        setErrorMessage('The signing session expired. Click Try again to reopen signing.');
        setNeedsFullResend(false);
        return;
      }
      if (eventName === 'id_check_failed') {
        setPhase('error');
        setErrorMessage(ID_VERIFICATION_FAILED_MESSAGE);
        setNeedsFullResend(true);
        return;
      }
      if (eventName === 'access_code_failed') {
        setPhase('error');
        setErrorMessage(
          'The access code could not be verified, so the rental agreement was not signed. Please try again, and if the problem continues contact Guest Experience.'
        );
        setNeedsFullResend(true);
        return;
      }
      if (eventName === 'exception') {
        setPhase('error');
        setErrorMessage(
          'Something went wrong during signing and the rental agreement was not completed. Please try again.'
        );
        setNeedsFullResend(false);
        return;
      }

      setPhase('error');
      setErrorMessage('Signing did not complete. Please try again.');
      setNeedsFullResend(false);
    },
    [callbackContinue, persistEnvelopeId]
  );

  const launchSigning = useCallback(
    async (storedEnvelopeId: string) => {
      const startResult = await startSigning({
        reservationName: reservation.name,
        envelopeId: storedEnvelopeId,
        returnUrl,
      });
      const startErr = (startResult as { error?: Error }).error;
      if (startErr) {
        setErrorMessage(startErr.message || 'Signing session could not be started.');
        setNeedsFullResend(true);
        setPhase('error');
        return false;
      }

      const { data } = startResult as { data?: { envelopeId: string; signingUrl: string } };
      if (!data?.signingUrl) {
        setErrorMessage('Signing session could not be started.');
        setNeedsFullResend(true);
        setPhase('error');
        return false;
      }

      persistEnvelopeId(data.envelopeId);
      setPhase('redirecting');
      window.location.assign(data.signingUrl);
      return true;
    },
    [persistEnvelopeId, reservation.name, returnUrl, startSigning]
  );

  const prepareAndLaunch = useCallback(
    async (voidExisting = true) => {
      setErrorMessage(null);
      setNeedsFullResend(false);
      setPhase('preparing');

      // The signer is the host's Salesforce record; the session profile's email can lag behind it.
      const hostEmail = mainGuest?.email?.trim();
      if (!hostEmail) {
        if (!missingHostEmailSnackShownRef.current) {
          missingHostEmailSnackShownRef.current = true;
          enqueueSnackbar(HOST_EMAIL_REQUIRED_SNACKBAR, { variant: 'warning' });
        }
        setPhase('error');
        setErrorMessage(HOST_EMAIL_REQUIRED_SNACKBAR);
        return;
      }

      const prepareResult = await prepareEmbedded({
        reservationName: reservation.name,
        returnUrl,
        voidExisting,
      });
      const prepareErr = (prepareResult as { error?: Error }).error;
      if (prepareErr) {
        setErrorMessage(prepareErr.message || 'Rental agreement could not be prepared.');
        setNeedsFullResend(false);
        setPhase('error');
        return;
      }

      const preparedEnvelopeId = (prepareResult as { data?: { envelopeId: string } }).data
        ?.envelopeId;
      if (!preparedEnvelopeId) {
        setErrorMessage('Rental agreement could not be prepared.');
        setPhase('error');
        return;
      }

      persistEnvelopeId(preparedEnvelopeId);
      if (voidExisting) {
        // The stale envelope is gone and this one replaces it, so a reload must not void it too.
        setRentalAgreementResignPending(reservation.name, false);
      }
      await launchSigning(preparedEnvelopeId);
    },
    [
      enqueueSnackbar,
      launchSigning,
      mainGuest,
      persistEnvelopeId,
      prepareEmbedded,
      reservation.name,
      returnUrl,
    ]
  );

  const retrySigning = useCallback(
    async (fullResend: boolean) => {
      // Always regenerate. Reopening a stored envelope (or Salesforce's existing open envelope)
      // would keep stale guest fields such as age.
      if (fullResend) {
        setResendCooldownUntil(Date.now() + RESEND_COOLDOWN_MS);
      }
      persistEnvelopeId(null);
      await prepareAndLaunch(true);
    },
    [persistEnvelopeId, prepareAndLaunch]
  );

  useEffect(() => {
    missingHostEmailSnackShownRef.current = false;
  }, [reservation.name]);

  // Consume DocuSign return event for cancel / id_check_failed (and rare no-identity signing_complete).
  useEffect(() => {
    const storedEvent = consumeDocuSignReturnEvent(reservation.name);
    if (!storedEvent || stepCompleted) return;

    hasAttemptedAutoPrepare.current = true;
    applyReturnEvent(storedEvent);
  }, [applyReturnEvent, reservation.name, stepCompleted]);

  useEffect(() => {
    if (stepCompleted || phase !== 'preparing' || hasAttemptedAutoPrepare.current) return;
    const userInfoLoaded = mainGuest !== null && sessionUser !== null;
    const stepAllowed =
      !enforceSessionIsMain ||
      (userInfoLoaded && mainGuest?.guest_id === sessionUser?.externalAccountID);
    if (!stepAllowed) return;
    if (prepareLoading || startLoading) return;

    hasAttemptedAutoPrepare.current = true;
    persistEnvelopeId(null);
    prepareAndLaunch(true).catch(() => {});
  }, [
    stepCompleted,
    phase,
    enforceSessionIsMain,
    persistEnvelopeId,
    mainGuest,
    sessionUser,
    prepareAndLaunch,
    prepareLoading,
    startLoading,
  ]);

  const userInfoLoaded = mainGuest !== null && sessionUser !== null;
  const stepAllowedForUser =
    !enforceSessionIsMain ||
    (userInfoLoaded && mainGuest?.guest_id === sessionUser?.externalAccountID);

  const isBusy = prepareLoading || startLoading || phase === 'preparing' || phase === 'redirecting';
  const now = Date.now();
  const resendOnCooldown = resendCooldownUntil > now;
  const cooldownSecondsRemaining = resendOnCooldown
    ? Math.max(0, Math.ceil((resendCooldownUntil - now) / 1000))
    : 0;

  useEffect(() => {
    if (!resendOnCooldown) {
      if (cooldownTimerId.current) {
        clearInterval(cooldownTimerId.current);
        cooldownTimerId.current = null;
      }
      return undefined;
    }
    if (cooldownTimerId.current) return undefined;
    cooldownTimerId.current = setInterval(() => setCooldownTick((t) => t + 1), 1000);
    return () => {
      if (cooldownTimerId.current) {
        clearInterval(cooldownTimerId.current);
        cooldownTimerId.current = null;
      }
    };
  }, [resendOnCooldown]);

  if (guestsLoading) {
    return (
      <FormProvider
        name="Rental Agreement Form"
        id={`Rental Agreement Form: ${reservation.name}`}
        methods={methods}
      >
        <Stack spacing={2} alignItems="center" py={4} minHeight={120}>
          <CircularProgress />
          <Typography>Loading…</Typography>
        </Stack>
      </FormProvider>
    );
  }

  if (enforceSessionIsMain && !userInfoLoaded) {
    return (
      <FormProvider
        name="Rental Agreement Form"
        id={`Rental Agreement Form: ${reservation.name}`}
        methods={methods}
      >
        <Alert severity="error">Main host not found.</Alert>
      </FormProvider>
    );
  }

  if (!stepAllowedForUser) {
    return (
      <FormProvider
        name="Rental Agreement Form"
        id={`Rental Agreement Form: ${reservation.name}`}
        methods={methods}
      >
        <Alert severity="error">
          You are not authorized to complete this step. Only the main host may complete the rental
          agreement.
        </Alert>
      </FormProvider>
    );
  }

  if (stepCompleted) {
    return (
      <FormProvider
        name="Rental Agreement Form"
        id={`Rental Agreement Form: ${reservation.name}`}
        methods={methods}
      >
        <Stack spacing={2}>
          <Alert severity="info" style={{ fontSize: '1.1rem' }}>
            The rental agreement has been signed.
          </Alert>
          <RegistrationFooter>
            <LoadingButton variant="contained" onClick={callbackContinue}>
              {stepFinal ? 'Finish' : 'Continue'}
            </LoadingButton>
          </RegistrationFooter>
        </Stack>
      </FormProvider>
    );
  }

  if (phase === 'error' && errorMessage) {
    return (
      <FormProvider
        name="Rental Agreement Form"
        id={`Rental Agreement Form: ${reservation.name}`}
        methods={methods}
      >
        <Stack spacing={2}>
          <Alert severity="error" style={{ fontSize: '1.1rem' }}>
            {errorMessage}
          </Alert>
          {brandConfig.helpLinks.guest_registration && (
            <HelpLink label="guest_registration" value={brandConfig.helpLinks.guest_registration} />
          )}
          <RegistrationFooter
            backButton={
              <Button disabled={isBusy} onClick={callbackCancel}>
                Back
              </Button>
            }
          >
            <Stack direction="row" spacing={2}>
              <Button disabled={isBusy} onClick={callbackStartOver}>
                Start Over
              </Button>
              <Button
                variant="contained"
                disabled={isBusy || (needsFullResend && resendOnCooldown)}
                onClick={() => {
                  retrySigning(needsFullResend).catch(() => {});
                }}
              >
                {needsFullResend && resendOnCooldown
                  ? `Re-send (${cooldownSecondsRemaining}s)`
                  : 'Try again'}
              </Button>
            </Stack>
          </RegistrationFooter>
        </Stack>
      </FormProvider>
    );
  }

  return (
    <FormProvider
      name="Rental Agreement Form"
      id={`Rental Agreement Form: ${reservation.name}`}
      methods={methods}
    >
      <Stack spacing={2} alignItems="center" py={4} minHeight={120}>
        <CircularProgress />
        <Typography>
          {phase === 'redirecting'
            ? 'Opening DocuSign to complete signing…'
            : 'Preparing your rental agreement…'}
        </Typography>
      </Stack>
    </FormProvider>
  );
}
